import { useState } from 'react'
import { Sparkles, Loader2 } from 'lucide-react'
import api from '../../services/api'
import { useTranslation } from 'react-i18next'

const BAND_STYLES = {
  healthy: 'bg-green-100 text-green-800 border-green-200',
  watch: 'bg-amber-100 text-amber-800 border-amber-200',
  at_risk: 'bg-red-100 text-red-800 border-red-200',
}
const BAND_LABELS = { healthy: 'Healthy', watch: 'Watch', at_risk: 'At Risk' }

/**
 * AIHealthBadge — on-demand 0-100 project health score in the project header.
 * Click "AI Health" → score badge with reasons tooltip panel.
 */
export default function AIHealthBadge({ projectId }) {
  const { t } = useTranslation('assistant')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showReasons, setShowReasons] = useState(false)

  const check = async () => {
    setLoading(true)
    try {
      const r = await api.get(`/ai/project-health/${projectId}`)
      setData(r.data)
      setShowReasons(true)
    } catch {
      setData({ error: 'failed' })
    } finally {
      setLoading(false)
    }
  }

  if (!data) {
    return (
      <button
        onClick={check}
        disabled={loading}
        className="flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
        title={t('t_health_score')}
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        AI Health
      </button>
    )
  }

  if (data.error || data.available === false) return null

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setShowReasons(s => !s)}
        className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border ${BAND_STYLES[data.band] || BAND_STYLES.watch}`}
      >
        <Sparkles className="w-3.5 h-3.5" />
        {data.score}/100 · {BAND_LABELS[data.band] || data.band}
      </button>
      {showReasons && (
        <div className="absolute left-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-4 z-40 text-left">
          <p className="text-xs font-semibold text-gray-700 mb-2">{t('why_this_score')}</p>
          <ul className="space-y-1.5 text-xs text-gray-600">
            {(data.reasons || []).map((r, i) => <li key={i}>• {r}</li>)}
          </ul>
          {data.next_step && (
            <p className="text-xs text-indigo-700 font-medium mt-2 pt-2 border-t border-gray-100">Next: {data.next_step}</p>
          )}
          <button onClick={() => setShowReasons(false)} className="text-[10px] text-gray-400 hover:text-gray-600 mt-2">close</button>
        </div>
      )}
    </div>
  )
}
